import { ThemeEvents } from '@theme/events'

const ALLOWED_BY_STATE = { CT: ['3mg'], RI: ['5mg'] }
const DEFAULT_ALLOWED = ['3mg', '5mg', '10mg']
const STATE_STORAGE_KEY = 'sola_product_state_selection'

function getStoredState() {
  try {
    return localStorage.getItem(STATE_STORAGE_KEY) || ''
  } catch (e) {
    return ''
  }
}

function setStoredState(state) {
  try {
    if (state) {
      localStorage.setItem(STATE_STORAGE_KEY, state)
    } else {
      localStorage.removeItem(STATE_STORAGE_KEY)
    }
  } catch (e) {
    // Ignore localStorage errors
  }
}

const q = (root, sel) => root.querySelector(sel)
const qa = (root, sel) => Array.from(root.querySelectorAll(sel))

function findStateSelect(form) {
  return q(form, 'select[data-state-selector]')
}

function getDoseFieldset(form) {
  return qa(form, 'fieldset.variant-option').find((fs) => {
    const lg = q(fs, 'legend')
    return lg && lg.textContent.trim().toLowerCase() === 'dose'
  })
}

function getDoseRadios(fieldset) {
  return fieldset ? qa(fieldset, 'input[type=radio]') : []
}

function getDoseDropdown(form) {
  const wrap = qa(form, '.variant-option.variant-option--dropdowns').find((div) => {
    const lb = q(div, 'label')
    return lb && lb.textContent.trim().toLowerCase() === 'dose'
  })
  return wrap ? q(wrap, 'select') : null
}

function allowedForState(state) {
  const key = (state || '').toUpperCase()
  return ALLOWED_BY_STATE[key] || DEFAULT_ALLOWED
}

function disableAllVariants(form) {
  qa(form, '.variant-option input[type=radio]').forEach((input) => {
    input.disabled = true
    input.setAttribute('aria-disabled', 'true')
    if (input.checked) {
      input.checked = false
      input.dataset.currentChecked = 'false'
    }
  })
  const select = getDoseDropdown(form)
  if (select) {
    Array.from(select.options).forEach((opt) => {
      if (opt.value) opt.disabled = true
    })
    select.value = ''
  }
}

function getUnavailableVariants(form, state, allowed) {
  const unavailable = []
  
  const fieldset = getDoseFieldset(form)
  const radios = getDoseRadios(fieldset)
  if (radios.length) {
    radios.forEach((radio) => {
      const v = String(radio.value).toLowerCase()
      const allowedHere = allowed.some((a) => a.toLowerCase() === v)
      if (!allowedHere && radio.value) {
        unavailable.push(radio.value)
      }
    })
  }
  
  const doseSelect = getDoseDropdown(form)
  if (doseSelect) {
    Array.from(doseSelect.options).forEach((opt) => {
      if (!opt.value) return
      const val = opt.value.trim().toLowerCase()
      const allowedHere = allowed.some((a) => a.toLowerCase() === val)
      if (!allowedHere && opt.value) {
        unavailable.push(opt.value)
      }
    })
  }
  
  return unavailable
}

function updateStateRestrictionMessage(picker, unavailableVariants, state) {
  // Remove existing message
  const existingMsg = picker.nextElementSibling?.classList.contains('state-restriction-message-wrapper')
    ? picker.nextElementSibling
    : null
  if (existingMsg) existingMsg.remove()
  
  // Only show message if state is selected and there are unavailable variants
  if (state && unavailableVariants.length > 0) {
    const wrapper = document.createElement('div')
    wrapper.className = 'state-restriction-message-wrapper'
    wrapper.style.cssText = 'margin-top: 0; padding: 0;'
    
    const message = document.createElement('div')
    message.className = 'state-restriction-message'
    message.style.cssText = 'font-size: 0.875rem; color: rgb(var(--color-foreground-rgb, 0 0 0) / 0.8); line-height: 1.5;'
    
    const unavailableList = unavailableVariants.join(', ')
    message.textContent = `Sorry, ${unavailableList} ${unavailableVariants.length === 1 ? 'is' : 'are'} not available for your state`
    
    wrapper.appendChild(message)
    
    // Insert after variant-picker
    if (picker.parentNode) {
      picker.parentNode.insertBefore(wrapper, picker.nextSibling)
    }
  }
}

function validateSelectedVariant(form, state, allowed) {
  // Don't uncheck variants - just mark them as unavailable
  // The add to cart button will be disabled instead
  // This allows users to see all variants but prevents purchasing restricted ones
  return false // No variant unchecked - we're using data-option-available instead
}

function applyStateRules(form) {
  const stateSelect = findStateSelect(form)
  // Check state from select, fallback to localStorage if empty (might be during morph)
  let state = stateSelect && stateSelect.value ? stateSelect.value : ''
  if (!state) {
    const storedState = getStoredState()
    if (storedState) {
      state = storedState
      console.log('[ApplyStateRules] Using stored state:', storedState)
      // Try to restore state in select if it exists but is empty
      if (stateSelect && !stateSelect.value) {
        setStateSelectValue(stateSelect, storedState)
      }
    }
  }
  
  const allowed = allowedForState(state)

  // If no state selected, disable all variants (keep existing behavior)
  if (!state) {
    disableAllVariants(form)
    const picker = form.closest('variant-picker')
    if (picker) {
      const existingMsg = picker.nextElementSibling?.classList.contains('state-restriction-message-wrapper')
        ? picker.nextElementSibling
        : null
      if (existingMsg) existingMsg.remove()
    }
    return
  }

  // State is selected - mark variants as available/unavailable instead of disabling
  const fieldset = getDoseFieldset(form)
  const radios = getDoseRadios(fieldset)
  if (radios.length) {
    radios.forEach((radio) => {
      const v = String(radio.value).toLowerCase()
      const allowedHere = allowed.some((a) => a.toLowerCase() === v)
      // Keep variants enabled, but mark availability
      radio.disabled = false
      radio.removeAttribute('aria-disabled')
      radio.setAttribute('data-option-available', allowedHere ? 'true' : 'false')
    })
  }

  const doseSelect = getDoseDropdown(form)
  if (doseSelect) {
    Array.from(doseSelect.options).forEach((opt) => {
      if (!opt.value) return
      const val = opt.value.trim().toLowerCase()
      const allowedHere = allowed.some((a) => a.toLowerCase() === val)
      // Keep options enabled, but mark availability
      opt.disabled = false
      opt.setAttribute('data-option-available', allowedHere ? 'true' : 'false')
    })
  }
  
  // Update restriction message after variant-picker
  const picker = form.closest('variant-picker')
  if (picker && state) {
    const unavailableVariants = getUnavailableVariants(form, state, allowed)
    updateStateRestrictionMessage(picker, unavailableVariants, state)
  } else if (picker) {
    // Remove message if no state selected
    const existingMsg = picker.nextElementSibling?.classList.contains('state-restriction-message-wrapper')
      ? picker.nextElementSibling
      : null
    if (existingMsg) existingMsg.remove()
  }
}

function updateAddToCart(form) {
  // Try multiple selectors to find the add to cart button
  const picker = form.closest('variant-picker')
  const productForm = picker?.closest('product-form-component')
  const atc = productForm?.querySelector('add-to-cart-component [ref="addToCartButton"]') || 
              productForm?.querySelector('button[type=submit][name=add]') ||
              document.querySelector(`button[type=submit][name=add][id*="BuyButtons"]`) ||
              document.querySelector('add-to-cart-component [ref="addToCartButton"]')
  
  const stateSelect = findStateSelect(form)
  // Check state from select, fallback to localStorage if empty (might be during morph)
  let stateValue = stateSelect?.value || ''
  if (!stateValue) {
    const storedState = getStoredState()
    if (storedState) {
      stateValue = storedState
      console.log('[AddToCart] Using stored state:', storedState)
      // If state select exists but is empty, try to restore it
      if (stateSelect && !stateSelect.value) {
        setStateSelectValue(stateSelect, storedState)
      }
    }
  }
  const stateSelected = !!stateValue
  
  // Check for selected variant - might need to wait a bit after morph
  const checkedRadio = form.querySelector('.variant-option input[type=radio]:checked')
  const doseSelect = getDoseDropdown(form)
  let variantSelected = !!checkedRadio || !!(doseSelect && doseSelect.value)
  
  // If no variant found, check if we're in the middle of a morph
  // Look for the variant ID in the form's script tag or check all radios
  if (!variantSelected) {
    // Check if any radio has data-current-checked="true" (set during morph)
    const currentChecked = form.querySelector('.variant-option input[type=radio][data-current-checked="true"]')
    if (currentChecked) {
      variantSelected = true
      console.log('[AddToCart] Found variant via data-current-checked')
    }
  }
  
  // Check if selected variant is available for the state
  let variantAvailable = true
  if (stateSelected && variantSelected) {
    // Use checkedRadio if found, otherwise use currentChecked fallback
    const radioToCheck = checkedRadio || form.querySelector('.variant-option input[type=radio][data-current-checked="true"]')
    
    if (radioToCheck) {
      const available = radioToCheck.getAttribute('data-option-available')
      variantAvailable = available !== 'false'
      console.log('[AddToCart] Radio variant available:', available, 'variantAvailable:', variantAvailable, 'radio:', radioToCheck.value)
    } else if (doseSelect && doseSelect.value) {
      const selectedOption = Array.from(doseSelect.options).find(opt => opt.value === doseSelect.value)
      if (selectedOption) {
        const available = selectedOption.getAttribute('data-option-available')
        variantAvailable = available !== 'false'
        console.log('[AddToCart] Dropdown variant available:', available, 'variantAvailable:', variantAvailable)
      }
    }
  }
  
  const shouldEnable = stateSelected && variantSelected && variantAvailable
  console.log('[AddToCart] State:', stateSelected, 'Variant:', variantSelected, 'Available:', variantAvailable, 'ShouldEnable:', shouldEnable)
  
  if (atc) {
    atc.disabled = !shouldEnable
    console.log('[AddToCart] Button disabled set to:', !shouldEnable, 'Button:', atc)
  } else {
    console.warn('[AddToCart] Button not found')
  }
}

function showStateRequiredMessage(form) {
  // Remove any existing message
  const existingMsg = form.querySelector('.state-required-message')
  if (existingMsg) existingMsg.remove()
  
  const message = document.createElement('div')
  message.className = 'state-required-message'
  message.textContent = 'Please select a state to proceed'
  message.style.cssText = 'color: var(--color-error, #d32f2f); margin-top: var(--margin-xs, 0.5rem); font-size: 0.875rem;'
  
  const stateSelect = findStateSelect(form)
  // Append to the variant-option container (parent of select-wrapper), not the select-wrapper itself
  if (stateSelect) {
    const selectWrapper = stateSelect.closest('.variant-option__select-wrapper')
    const variantOption = stateSelect.closest('.variant-option--dropdowns')
    if (variantOption) {
      variantOption.appendChild(message)
      // Remove message after 3 seconds
      setTimeout(() => message.remove(), 3000)
    }
  }
}

function setStateSelectValue(stateSelect, value) {
  if (!stateSelect || !value) return
  stateSelect.value = value
  // Also set the selected attribute on the option element
  Array.from(stateSelect.options).forEach((opt) => {
    opt.removeAttribute('selected')
    if (opt.value === value) {
      opt.setAttribute('selected', 'selected')
    }
  })
}

function initForm(form, preserveState = null, skipDisable = false) {
  if (form.dataset.stateInitialized === 'true') {
    // If form is already initialized but we need to preserve state, do it now
    if (preserveState) {
      const stateSelect = findStateSelect(form)
      if (stateSelect) {
        setStateSelectValue(stateSelect, preserveState)
        applyStateRules(form)
        updateAddToCart(form)
      }
    }
    return
  }
  form.dataset.stateInitialized = 'true'
  
  const stateSelect = findStateSelect(form)
  let currentState = null
  
  if (stateSelect) {
    // If preserveState is provided, use it; otherwise check if state is already set or in localStorage
    if (preserveState) {
      setStateSelectValue(stateSelect, preserveState)
      currentState = preserveState
      setStoredState(preserveState) // Update localStorage
    } else {
      currentState = stateSelect.value || ''
      // If no state in select, try to restore from localStorage
      if (!currentState) {
        const storedState = getStoredState()
        if (storedState) {
          console.log('[Init] Restoring state from localStorage:', storedState)
          setStateSelectValue(stateSelect, storedState)
          currentState = storedState
        } else {
          Array.from(stateSelect.options).forEach((o) => o.removeAttribute('selected'))
          stateSelect.value = ''
        }
      } else {
        // State is set, update localStorage
        setStoredState(currentState)
      }
    }
  }
  
  // Disable all variants if no state is selected (on initial load)
  // If state is preserved, skip disabling since variants are already set by server
  if (!skipDisable) {
    if (!currentState) {
      disableAllVariants(form)
    } else {
      // State is set, apply rules (variants will be marked available/unavailable)
      applyStateRules(form)
      // Use setTimeout to ensure attributes are set before checking
      setTimeout(() => {
        updateAddToCart(form)
      }, 0)
    }
  } else if (currentState) {
    // Even if skipDisable, apply state rules when state is preserved
    applyStateRules(form)
    // Use setTimeout to ensure attributes are set before checking
    setTimeout(() => {
      updateAddToCart(form)
    }, 0)
  } else {
    // No state but skipDisable is true - still check localStorage and apply rules
    const storedState = getStoredState()
    if (storedState && stateSelect) {
      setStateSelectValue(stateSelect, storedState)
      currentState = storedState
      applyStateRules(form)
      setTimeout(() => {
        updateAddToCart(form)
      }, 0)
    } else {
      // No state at all, update add to cart to disable it
      setTimeout(() => {
        updateAddToCart(form)
      }, 0)
    }
  }
  
  if (stateSelect) {
    stateSelect.addEventListener('change', () => {
      const state = stateSelect.value
      console.log('[State] Selected', state)
      // Save to localStorage
      setStoredState(state)
      if (ALLOWED_BY_STATE[state?.toUpperCase()]) {
        console.log('[State] Restricted', state, 'allowed doses', ALLOWED_BY_STATE[state.toUpperCase()])
      } else {
        console.log('[State] Allowed default', DEFAULT_ALLOWED)
      }
      // Apply state rules (will mark variants as available/unavailable)
      applyStateRules(form)
      updateAddToCart(form)
    })
    stateSelect.addEventListener('change', (e) => {
      e.stopPropagation()
    })
  }
  const picker = form.closest('variant-picker')
  if (picker) {
    // Watch for state select value changes and restore from localStorage if needed
    // This handles cases where the select gets reset during morphing
    const stateSelect = findStateSelect(form)
    if (stateSelect) {
      const watchForStateReset = () => {
        const storedState = getStoredState()
        if (storedState && stateSelect.value !== storedState) {
          console.log('[State Watch] Detected state reset, restoring from localStorage:', storedState)
          setStateSelectValue(stateSelect, storedState)
          applyStateRules(form)
        }
      }
      
      // Watch the state select for value changes
      const stateObserver = new MutationObserver((mutations) => {
        // Only restore if the value actually changed to empty
        const currentValue = stateSelect.value
        const storedState = getStoredState()
        if (storedState && (!currentValue || currentValue === '')) {
          // Debounce to avoid excessive calls
          clearTimeout(stateSelect._restoreTimeout)
          stateSelect._restoreTimeout = setTimeout(watchForStateReset, 10)
        }
      })
      
      stateObserver.observe(stateSelect, {
        attributes: true,
        attributeFilter: ['value'],
        childList: false,
        subtree: false
      })
      
      // Also watch for option selected attribute changes
      const optionObserver = new MutationObserver(() => {
        const storedState = getStoredState()
        if (storedState && stateSelect.value !== storedState) {
          clearTimeout(stateSelect._restoreTimeout)
          stateSelect._restoreTimeout = setTimeout(watchForStateReset, 10)
        }
      })
      
      optionObserver.observe(stateSelect, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['selected']
      })
    }
    
    // Validate state selection before variant changes
    // Use capture phase ONLY to block when no state, otherwise let event flow normally
    form.addEventListener('change', (e) => {
      // Skip state selector - it has its own handler
      if (e.target.closest('select[data-state-selector]')) return
      
      // Only validate variant inputs
      const isVariantInput = e.target.matches('.variant-option input[type=radio]') || 
                            (e.target instanceof HTMLSelectElement && e.target.closest('.variant-option--dropdowns') && !e.target.closest('[data-state-selector]'))
      
      if (isVariantInput) {
        // Get the current form (might have changed due to morphing)
        const currentForm = e.target.closest('.variant-picker__form') || form
        const stateSelect = findStateSelect(currentForm)
        // Get state value from select, fallback to localStorage if empty
        let state = stateSelect?.value?.trim() || ''
        if (!state) {
          // Try to restore from localStorage
          const storedState = getStoredState()
          if (storedState && stateSelect) {
            console.log('[Validation] Restoring state from localStorage:', storedState)
            setStateSelectValue(stateSelect, storedState)
            state = storedState
            // Don't apply state rules here - let the variant change complete first
            // State rules will be applied after variant update
          }
        }
        
        if (!state) {
          // ONLY block when no state - prevent the change from happening
          console.log('[Validation] Blocking variant selection - no state selected')
          e.preventDefault()
          e.stopImmediatePropagation()
          showStateRequiredMessage(currentForm)
          // Reset the selection immediately
          if (e.target instanceof HTMLInputElement) {
            e.target.checked = false
            e.target.dataset.currentChecked = 'false'
          } else if (e.target instanceof HTMLSelectElement) {
            e.target.value = ''
            Array.from(e.target.options).forEach(opt => {
              opt.removeAttribute('selected')
              if (!opt.value) opt.setAttribute('selected', 'selected')
            })
          }
          updateAddToCart(currentForm)
          return false
        }
        // State IS selected - do NOTHING, let event continue to variant-picker
        // The event will bubble normally and variant-picker will handle it
        console.log('[Validation] Allowing variant selection - state is:', state, 'variant:', e.target.value || e.target.dataset.optionValueId)
        // Explicitly do NOT prevent or stop - let it bubble to variant-picker
        // Update add to cart button after variant selection (will check data-option-available)
        // Use setTimeout to ensure variant selection completes first
        setTimeout(() => {
          updateAddToCart(currentForm)
        }, 0)
        return // Allow event to continue
      }
      // Not a variant input - let it through
    }, { capture: true })
    
    // Listen to variant events but DO NOT stop propagation
    // This allows theme's default behavior (price updates, image changes) to work normally
    picker.addEventListener(ThemeEvents.variantSelected, (e) => {
      console.log('[Variant] Selected', e.detail?.resource?.id)
      // Update add to cart button - it will check if variant is available for state
      updateAddToCart(form)
      // CRITICAL: Don't stop propagation - let other listeners (price, image, etc.) receive the event
      // Do NOT call e.stopPropagation() or e.stopImmediatePropagation()
    })
    picker.addEventListener(ThemeEvents.variantUpdate, (e) => {
      const variantId = e.detail?.resource?.id
      console.log('[Variant] Updated', variantId, 'product', e.detail?.data?.productId)
      // Don't stop propagation - let other listeners (price, image, etc.) receive the event
      const newForm = picker.querySelector('.variant-picker__form')
      
      if (newForm && newForm !== form) {
        // Preserve state selection BEFORE form is morphed
        const preservedState = findStateSelect(form)?.value || ''
        console.log('[Variant Update] Preserving state:', preservedState)
        
        // Update form reference
        form = newForm
        
        // Use localStorage as fallback if preservedState is empty
        const stateToRestore = preservedState || getStoredState()
        
        // Restore state IMMEDIATELY and aggressively
        if (stateToRestore) {
          const restoreStateInDropdown = () => {
            const stateSelect = findStateSelect(form)
            if (!stateSelect) {
              // State select doesn't exist yet, try again later
              return false
            }
            
            const currentValue = stateSelect.value || ''
            if (currentValue !== stateToRestore) {
              setStateSelectValue(stateSelect, stateToRestore)
              setStoredState(stateToRestore)
              console.log('[Variant Update] State restored in dropdown to:', stateSelect.value)
              // Apply state rules (will mark variants as available/unavailable)
              applyStateRules(form)
              // Update add to cart after state is set and rules are applied
              // Use longer timeout to ensure DOM is fully updated
              setTimeout(() => {
                updateAddToCart(form)
              }, 10)
              return true // State was restored
            }
            // Even if state is already set, ensure rules are applied
            if (currentValue === stateToRestore) {
              applyStateRules(form)
              // Update add to cart after rules are applied
              // Use longer timeout to ensure DOM is fully updated
              setTimeout(() => {
                updateAddToCart(form)
              }, 10)
            }
            return false // State already set
          }
          
          // Restore immediately (synchronous) - before any other code runs
          restoreStateInDropdown()
          
          // Initialize the new form with preserved state
          if (newForm.dataset.stateInitialized !== 'true') {
            initForm(newForm, stateToRestore, true) // skipDisable = true to avoid resetting variants
          }
          
          // Restore multiple times to ensure it sticks after morph completes
          // Use microtask, then animation frames, then timeouts
          Promise.resolve().then(() => {
            restoreStateInDropdown()
          })
          
          requestAnimationFrame(() => {
            restoreStateInDropdown()
            requestAnimationFrame(() => {
              restoreStateInDropdown()
              setTimeout(() => {
                restoreStateInDropdown()
                setTimeout(() => {
                  restoreStateInDropdown()
                  // Final check after longer delay - ensure everything is ready
                  setTimeout(() => {
                    restoreStateInDropdown()
                    // One more check after state is definitely restored
                    setTimeout(() => {
                      const stateSelect = findStateSelect(form)
                      if (stateSelect && stateSelect.value === stateToRestore) {
                        applyStateRules(form)
                        // Ensure variant is still selected after all the restoration
                        const checkedRadio = form.querySelector('.variant-option input[type=radio]:checked')
                        if (!checkedRadio) {
                          // Try to find and check the variant that was just updated
                          const variantId = e.detail?.resource?.id
                          if (variantId) {
                            const radioForVariant = form.querySelector(`.variant-option input[type=radio][data-variant-id="${variantId}"]`)
                            if (radioForVariant && !radioForVariant.disabled) {
                              radioForVariant.checked = true
                              radioForVariant.setAttribute('data-current-checked', 'true')
                              console.log('[Variant Update] Re-checked variant radio after morph:', variantId)
                            }
                          }
                        }
                        updateAddToCart(form)
                      }
                    }, 50)
                  }, 300)
                }, 100)
              }, 50)
            })
          })
        } else {
          // No state to restore, just initialize
          if (newForm.dataset.stateInitialized !== 'true') {
            initForm(newForm, null, true)
          }
          // Update add to cart even if no state
          setTimeout(() => {
            updateAddToCart(form)
          }, 0)
        }
      } else {
        // Same form, just apply rules to ensure state restrictions are still applied
        const currentState = findStateSelect(form)?.value || getStoredState()
        if (currentState) {
          requestAnimationFrame(() => {
            applyStateRules(form)
            updateAddToCart(form)
          })
        } else {
          // No state, but still update add to cart
          setTimeout(() => {
            updateAddToCart(form)
          }, 0)
        }
      }
      // CRITICAL: Don't stop propagation - let other listeners (price, image, etc.) receive the event
      // Do NOT call e.stopPropagation() or e.stopImmediatePropagation()
      // The VariantUpdateEvent must bubble to product-price, product-form, etc.
    })
  }
}

// Initialize all forms on page load
Array.from(document.querySelectorAll('.variant-picker__form')).forEach(initForm)

// Also check URL variant parameter on page load
function checkUrlVariant() {
  // No need to uncheck variants - they'll be marked as unavailable
  // The add to cart button will be disabled if variant is restricted
  // Just ensure state rules are applied
  const forms = Array.from(document.querySelectorAll('.variant-picker__form'))
  forms.forEach(form => {
    const stateSelect = findStateSelect(form)
    const state = stateSelect?.value || getStoredState()
    if (state) {
      applyStateRules(form)
      // Use setTimeout to ensure attributes are set before checking
      setTimeout(() => {
        updateAddToCart(form)
      }, 0)
    } else {
      // Even if no state, update add to cart to disable it
      updateAddToCart(form)
    }
  })
}

// Check URL variant after a short delay to ensure DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(checkUrlVariant, 100)
  })
} else {
  setTimeout(checkUrlVariant, 100)
}
